import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, ArrowRight } from "lucide-react";
import { posts, getFeatured } from "@/data/posts";
import {
  Container,
  Section,
  Eyebrow,
  Reveal,
  MaskedLines,
  GlowOrb,
} from "@/components/kit";
import { CtaBand } from "@/components/CtaBand";

export default function Blog() {
  const featured = getFeatured();
  const rest = posts.filter((p) => p.slug !== featured.slug);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-14 md:pt-44 md:pb-16">
        <GlowOrb className="left-1/2 top-0 h-[460px] w-[680px] -translate-x-1/2 opacity-80" />
        <div className="absolute inset-0 -z-10 grain opacity-[0.035]" />
        <Container>
          <Eyebrow>Insights</Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98] text-white">
            <MaskedLines lines={["The outbound", "playbook —"]} delay={0.1} />
            <MaskedLines
              lines={["field notes from the lab"]}
              lineClassName="text-gold text-glow-gold"
              delay={0.1 + 2 * 0.11}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/65"
          >
            Tactics, frameworks, and hard-won lessons on ICP research, deliverability, and booking
            qualified B2B meetings that actually convert.
          </motion.p>
        </Container>
      </section>

      {/* Featured */}
      <Section className="pt-4 md:pt-6">
        <Container>
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="group grid overflow-hidden rounded-3xl border border-white/10 bg-navy-800 lg:grid-cols-2 transition-[border-color,box-shadow] duration-300 hover:border-gold/50 hover:shadow-[0_0_50px_rgba(255,215,0,0.12)]"
            >
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img
                  src={featured.cover}
                  alt={featured.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-800 via-navy-800/20 to-transparent lg:bg-gradient-to-r" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4">
                  <span className="rounded-full border border-gold/25 bg-gold/5 px-3 py-1 font-mono-jb text-[11px] uppercase tracking-widest text-gold/80">
                    Featured · {featured.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono-jb text-xs text-white/45">
                    <Clock className="h-3.5 w-3.5" /> {featured.readTime}
                  </span>
                </div>
                <h2 className="mt-6 font-display text-2xl md:text-4xl font-medium tracking-tight text-white">
                  {featured.title}
                </h2>
                <p className="mt-4 text-white/60 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 font-semibold text-gold">
                  Read the playbook
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* Grid */}
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-navy-800 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_0_40px_rgba(255,215,0,0.12)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-800 to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-navy-900/70 backdrop-blur px-3 py-1 font-mono-jb text-[10px] uppercase tracking-widest text-white/70">
                      {p.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center gap-3 font-mono-jb text-[11px] text-white/40">
                      <span>{p.date}</span>
                      <span className="h-1 w-1 rounded-full bg-white/30" />
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {p.readTime}
                      </span>
                    </div>
                    <h3 className="mt-3 font-display text-lg font-semibold leading-snug text-white group-hover:text-gold transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55 line-clamp-3">
                      {p.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 font-mono-jb text-xs text-gold/80">
                      Read more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CtaBand
        eyebrow="From reading to results"
        title="Enough theory."
        titleAccent="Let's book you meetings."
        subtitle="Put these playbooks to work. Book a discovery call and we'll build the outbound engine behind your next quarter of qualified pipeline."
      />
    </div>
  );
}
